import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown, ArrowUp, Building2, ChevronDown, Copy, Eye, EyeOff, FolderPlus, GripVertical, Pencil, Pin, Share2, Trash2, Users, X,
} from 'lucide-react';
import type { Layer } from '@/types/api';
import workspaceService, { CompanyLayer } from '@/services/workspace.service';
import { GroupItem, LayerGroup, toInput } from '@/services/layerGroups.service';
import { useMapStore } from '@/store/map.store';
import { hide, isShown, show } from '@/utils/companyLayers';
import { cn } from '@/utils/cn';
import { useLayerGroups, useMapLayerChanges } from './useLayerGroups';
import ShareGroupPanel from './ShareGroupPanel';

/**
 * "My menus": the layer groups I made and those shared with me, above the usual layer tree. A group is a view onto
 * layers that are there anyway - its eyes switch the very same map layers the tree does (a platform layer through the
 * layer store, a company layer on the map), so nothing is drawn twice. Only the owner changes a group; anyone may
 * fold or hide one for themselves, and copy one shared with them into their own.
 */

const small = 'p-0.5 rounded hover:bg-gn-tertiary text-gn-secondary disabled:opacity-40';
const input = 'w-full min-w-0 px-2 py-1 border border-gn-light rounded bg-gn-surface text-gn-primary text-xs';

interface Props {
  /** The platform layers the panel lists (the layer store's), to switch them through it. */
  platformLayers: Layer[];
  visibility: Record<number, boolean>;
  onTogglePlatform: (layerId: number) => void;
}

export const MyMenusPanel: React.FC<Props> = ({ platformLayers, visibility, onTogglePlatform }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').substring(0, 2);
  const groups = useLayerGroups();
  const map = useMapStore((s) => s.mapInstance);
  useMapLayerChanges(map);
  const [open, setOpen] = useState(true);
  const [newName, setNewName] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [dragGroup, setDragGroup] = useState<string | null>(null);

  // The company layers as the workspaces list them (the same query WorkspacePanel reads), to draw one when switched on.
  const { data: companyLayers = [] } = useQuery<CompanyLayer[]>({
    queryKey: ['workspaceLayers', 'all'], queryFn: () => workspaceService.allLayers(), retry: false,
  });

  const data = groups.query.data;
  if (groups.query.isError || !data) return null; // no groups here (a platform sign-in, or the server is older)
  const listed = data.groups.filter((g) => !g.hidden);
  const hidden = data.groups.filter((g) => g.hidden);
  const mineIds = data.groups.filter((g) => g.mine).map((g) => g.id);

  const dropGroup = (target: string) => {
    if (!dragGroup || dragGroup === target) return;
    const ids = mineIds.filter((id) => id !== dragGroup);
    ids.splice(ids.indexOf(target), 0, dragGroup);
    groups.order.mutate(ids);
    setDragGroup(null);
  };

  return (
    <div className="border-b border-gn-light" data-testid="my-menus">
      <button type="button" onClick={() => setOpen(!open)}
              className="flex items-center justify-between w-full px-3 py-2 rounded hover:bg-gn-tertiary text-xs font-medium text-gn-primary">
        <span className="flex items-center gap-2">
          {t('layerGroups.title')}
          {data.groups.length > 0 && <span className="text-[10px] text-gn-muted">({listed.length})</span>}
        </span>
        <ChevronDown size={14} className={cn('transition-transform text-gn-secondary', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="px-2 pb-2 space-y-1">
          {data.groups.length === 0 && <p className="text-[10px] text-gn-muted italic px-1">{t('layerGroups.empty_hint')}</p>}
          {listed.map((g) => (
            <GroupBlock key={g.id} group={g} lang={lang} groups={groups} platformLayers={platformLayers} visibility={visibility}
                        onTogglePlatform={onTogglePlatform} companyLayers={companyLayers} canCreate={data.canCreate}
                        dragging={dragGroup} onDragStart={() => setDragGroup(g.id)} onDrop={() => dropGroup(g.id)} />
          ))}
          {hidden.length > 0 && (
            <div className="pt-1">
              <button type="button" className="text-[10px] text-gn-muted hover:text-gn-primary" onClick={() => setShowHidden(!showHidden)}>
                {t('layerGroups.hidden_count', { count: hidden.length })}
              </button>
              {showHidden && hidden.map((g) => (
                <div key={g.id} className="flex items-center gap-1 text-xs pl-2">
                  <span className="flex-1 truncate text-gn-secondary">{g.name}</span>
                  <button type="button" className={small} title={t('layerGroups.unhide')} onClick={() => groups.view.mutate({ id: g.id, hidden: false })}>
                    <Eye size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {data.canCreate && (
            <form className="flex gap-1 pt-1" onSubmit={(e) => {
              e.preventDefault();
              if (newName.trim()) groups.create.mutate({ name: newName.trim() }, { onSuccess: () => setNewName('') });
            }}>
              <input className={input} placeholder={t('layerGroups.new')} value={newName} maxLength={120}
                     onChange={(e) => setNewName(e.target.value)} aria-label={t('layerGroups.new')} />
              <button type="submit" className="p-1 rounded hover:bg-gn-tertiary text-gn-secondary disabled:opacity-40"
                      disabled={!newName.trim() || groups.create.isPending} title={t('layerGroups.create')}>
                <FolderPlus size={14} />
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

const GroupBlock: React.FC<{
  group: LayerGroup;
  lang: string;
  groups: ReturnType<typeof useLayerGroups>;
  platformLayers: Layer[];
  visibility: Record<number, boolean>;
  onTogglePlatform: (layerId: number) => void;
  companyLayers: CompanyLayer[];
  canCreate: boolean;
  dragging: string | null;
  onDragStart: () => void;
  onDrop: () => void;
}> = ({ group, lang, groups, platformLayers, visibility, onTogglePlatform, companyLayers, canCreate, dragging, onDragStart, onDrop }) => {
  const { t } = useTranslation();
  const map = useMapStore((s) => s.mapInstance);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(group.name);
  const [sharing, setSharing] = useState(false);
  const [dragItem, setDragItem] = useState<number | null>(null);
  const collapsed = group.collapsed;

  const platformOf = (i: GroupItem) => platformLayers.find((l) => l.id === i.layer.number);
  const companyOf = (i: GroupItem) => companyLayers.find((l) => l.key === i.layer.key && !l.dataOnly);
  const isOn = (i: GroupItem): boolean => {
    if (i.kind === 'platform') return !!(i.layer.number != null && visibility[i.layer.number]);
    const c = companyOf(i);
    return !!c && isShown(map, c);
  };
  const canToggle = (i: GroupItem) => (i.kind === 'platform' ? !!platformOf(i) : !!map && !!companyOf(i));
  const toggle = (i: GroupItem, on?: boolean) => {
    const now = isOn(i);
    if (on !== undefined && on === now) return;
    if (i.kind === 'platform') {
      if (i.layer.number != null && platformOf(i)) onTogglePlatform(i.layer.number);
      return;
    }
    const c = companyOf(i);
    if (!map || !c) return;
    if (now) hide(map, c);
    else show(map, c);
  };
  const anyOn = group.items.some(isOn);
  const nameOf = (i: GroupItem) => i.label || i.layer.labels?.[lang] || i.layer.labels?.en || i.ref;

  const saveItems = (items: GroupItem[]) => groups.change.mutate({ id: group.id, change: { items: items.map(toInput) } });
  const move = (from: number, to: number) => {
    if (to < 0 || to >= group.items.length || from === to) return;
    const items = [...group.items];
    const [it] = items.splice(from, 1);
    items.splice(to, 0, it);
    saveItems(items);
  };

  return (
    <div className={cn('border border-gn-light rounded', dragging === group.id && 'opacity-50')} data-testid="layer-group"
         draggable={group.mine && !renaming} onDragStart={(e) => { if (group.mine) { e.dataTransfer.effectAllowed = 'move'; onDragStart(); } }}
         onDragOver={(e) => { if (group.mine && dragging) e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); onDrop(); }}>
      <div className="group flex items-center gap-1 px-1 py-1">
        {group.mine && <GripVertical size={12} className="text-gn-muted cursor-grab shrink-0" aria-hidden="true" />}
        <button type="button" className={small} onClick={() => groups.view.mutate({ id: group.id, collapsed: !collapsed })}
                aria-expanded={!collapsed} title={collapsed ? t('layerGroups.expand') : t('layerGroups.collapse')}>
          <ChevronDown size={12} className={cn('transition-transform', !collapsed && 'rotate-180')} />
        </button>
        {group.colour && <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: group.colour }} />}
        {renaming ? (
          <form className="flex-1 min-w-0" onSubmit={(e) => {
            e.preventDefault();
            if (name.trim() && name.trim() !== group.name) groups.change.mutate({ id: group.id, change: { name: name.trim() } });
            setRenaming(false);
          }}>
            <input className={input} value={name} autoFocus maxLength={120} onChange={(e) => setName(e.target.value)}
                   onBlur={() => setRenaming(false)} onKeyDown={(e) => { if (e.key === 'Escape') setRenaming(false); }} />
          </form>
        ) : (
          <span className="flex-1 min-w-0 truncate text-xs font-medium text-gn-primary" title={group.description ?? group.name}
                onDoubleClick={() => { if (group.editable) { setName(group.name); setRenaming(true); } }}>
            {group.name}
            {!group.mine && (
              <span className="ml-1 text-[10px] font-normal text-gn-muted" data-testid="group-owner">
                {t('layerGroups.by', { name: group.ownerName || t('layerGroups.colleague') })}
              </span>
            )}
          </span>
        )}
        {group.mine && group.shared && (
          <span className="text-gn-blue" title={group.sharing?.company ? t('layerGroups.shared_company') : t('layerGroups.shared_people', { count: group.sharing?.people.length ?? 0 })}>
            {group.sharing?.company ? <Building2 size={11} /> : <Users size={11} />}
          </span>
        )}
        {!group.mine && (
          <span className="text-gn-muted" title={group.sharedWith === 'company' ? t('layerGroups.shared_with_company') : t('layerGroups.shared_with_you')}>
            {group.sharedWith === 'company' ? <Building2 size={11} /> : <Users size={11} />}
          </span>
        )}
        <button type="button" className={small} disabled={group.items.length === 0}
                title={anyOn ? t('layerGroups.hide_all') : t('layerGroups.show_all')}
                onClick={() => group.items.filter(canToggle).forEach((i) => toggle(i, !anyOn))}>
          {anyOn ? <Eye size={12} className="text-emerald-500" /> : <EyeOff size={12} />}
        </button>
        {group.editable && (
          <button type="button" className={small} title={t('layerGroups.rename')} onClick={() => { setName(group.name); setRenaming(true); }}>
            <Pencil size={12} />
          </button>
        )}
        {group.canShare && (
          <button type="button" className={cn(small, sharing && 'bg-gn-tertiary')} title={t('layerGroups.share')} onClick={() => setSharing(!sharing)}
                  data-testid="share-group">
            <Share2 size={12} />
          </button>
        )}
        {!group.mine && canCreate && (
          <button type="button" className={small} title={t('layerGroups.copy')} data-testid="copy-group"
                  onClick={() => groups.copy.mutate({ id: group.id, name: t('layerGroups.copy_name', { name: group.name }) })}>
            <Copy size={12} />
          </button>
        )}
        {!group.mine && (
          <button type="button" className={small} title={t('layerGroups.hide_group')} onClick={() => groups.view.mutate({ id: group.id, hidden: true })}>
            <X size={12} />
          </button>
        )}
        {group.mine && (
          <button type="button" className={small} title={t('layerGroups.delete')}
                  onClick={() => { if (window.confirm(t('layerGroups.delete_confirm', { name: group.name }))) groups.remove.mutate(group.id); }}>
            <Trash2 size={12} />
          </button>
        )}
      </div>
      {sharing && (
        <ShareGroupPanel group={group} saving={groups.share.isPending} onClose={() => setSharing(false)}
                         onSave={(company, people) => groups.share.mutate({ id: group.id, company, people }, { onSuccess: () => setSharing(false) })} />
      )}
      {!collapsed && (
        <div className="px-1 pb-1 space-y-0.5" role="group" aria-label={group.name}>
          {group.items.length === 0 && (
            <p className="text-[10px] text-gn-muted italic px-2 py-0.5">{group.mine ? t('layerGroups.empty_group') : t('layerGroups.nothing_visible')}</p>
          )}
          {group.items.map((i, index) => {
            const on = isOn(i);
            return (
              <div key={`${i.kind}:${i.ref}`} data-testid="group-item"
                   className={cn('group flex items-center gap-1 pl-4 pr-1 py-1 rounded text-xs', on ? 'bg-gn-tertiary' : 'hover:bg-gn-tertiary',
                     dragItem === index && 'opacity-50')}
                   draggable={group.editable} onDragStart={(e) => { e.stopPropagation(); setDragItem(index); }}
                   onDragOver={(e) => { if (dragItem !== null) { e.preventDefault(); e.stopPropagation(); } }}
                   onDrop={(e) => { if (dragItem !== null) { e.preventDefault(); e.stopPropagation(); move(dragItem, index); setDragItem(null); } }}>
                <button type="button" className={cn(small, on && 'text-emerald-500')} disabled={!canToggle(i)} onClick={() => toggle(i)}
                        title={on ? t('layerGroups.hide_layer') : t('layerGroups.show_layer')}>
                  {on ? <Eye size={12} /> : <EyeOff size={12} />}
                </button>
                <span className={cn('flex-1 truncate text-[11px]', on ? 'text-gn-primary font-medium' : 'text-gn-secondary')} title={i.ref}>
                  {nameOf(i)}
                </span>
                {i.kind === 'company' && <span className="text-[9px] uppercase text-gn-muted">{t('layerGroups.company_badge')}</span>}
                {group.editable && (
                  <span className="flex items-center opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                    <button type="button" className={small} disabled={index === 0} title={t('layerGroups.up')} onClick={() => move(index, index - 1)}>
                      <ArrowUp size={11} />
                    </button>
                    <button type="button" className={small} disabled={index === group.items.length - 1} title={t('layerGroups.down')}
                            onClick={() => move(index, index + 1)}>
                      <ArrowDown size={11} />
                    </button>
                    <button type="button" className={small} title={t('layerGroups.rename_item')} onClick={() => {
                      const label = window.prompt(t('layerGroups.rename_item_prompt'), nameOf(i));
                      if (label === null) return;
                      saveItems(group.items.map((x, n) => (n === index ? { ...x, label: label.trim() || null } : x)));
                    }}>
                      <Pencil size={11} />
                    </button>
                    <button type="button" className={small} title={t('layerGroups.remove_item')}
                            onClick={() => saveItems(group.items.filter((_, n) => n !== index))}>
                      <X size={11} />
                    </button>
                  </span>
                )}
                {group.editable && (
                  <button type="button" className={cn(small, i.onlyHere ? 'text-gn-blue' : 'opacity-0 group-hover:opacity-100')}
                          title={i.onlyHere ? t('layerGroups.only_here_on') : t('layerGroups.only_here_off')} aria-pressed={i.onlyHere}
                          onClick={() => saveItems(group.items.map((x, n) => (n === index ? { ...x, onlyHere: !x.onlyHere } : x)))}>
                    <Pin size={11} fill={i.onlyHere ? 'currentColor' : 'none'} />
                  </button>
                )}
              </div>
            );
          })}
          {group.mine && !!group.unavailable && (
            <p className="text-[10px] text-gn-muted italic px-2">{t('layerGroups.unavailable', { count: group.unavailable })}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default MyMenusPanel;
